import { pxToRem } from '@fluentsms/agentnet-web-components';
import { Grid, Theme, Typography } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import { getIntelliCheckReport } from 'api/file/file-api';
import { IntelliCheckReportResponse } from 'api/file/interfaces/get-file-order-insights';
import { useSearchParams } from 'features/help/hooks/useSearchParams';
import useAsync from 'hooks/useAsync';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { normalizeKeysToCamelCase } from 'utilities/utilities';
import BarcodeScanInfo from './components/BarcodeScanInfo';
import ScanImages from './components/ScanImages';
import SummaryInfo from './components/SummaryInfo';

const IntellicheckReport = () => {
  const { fileId, transactionId } = useParams<{ fileId: string; transactionId: string }>();
  const { pdfToken } = useSearchParams<{ pdfToken?: string }>();
  const [reportData, setReportData] = useState<IntelliCheckReportResponse | null>(null);

  const getIntelliCheckReportData = async (): Promise<IntelliCheckReportResponse> => {
    return getIntelliCheckReport(
      fileId ?? '',
      pdfToken ?? '',
      transactionId ?? '',
    ) as Promise<IntelliCheckReportResponse>;
  };
  const { execute: executeIntelliCheckReport, value: intelliCheckReportData } = useAsync<IntelliCheckReportResponse>(
    getIntelliCheckReportData,
    false,
  );

  const useStyles = makeStyles((theme: Theme) =>
    createStyles({
      fullWidth: {
        width: '100%',
        background: theme.palette.background.paper,
      },
      pdfbody: {
        width: '8.5in',
        padding: '0',
        margin: theme.spacing(0, 'auto'),
        background: `${theme.palette.background.paper} !important`,
        border: `6px solid ${theme.palette.background.paper} !important`,
        boxShadow: 'none !important',
      },
      pageBreak: {
        pageBreakAfter: 'always',
        display: 'flex',
        justifyContent: 'center',
        flexDirection: 'column',
        height: '8.5in',
        paddingTop: '1in',
      },
      blueBar: {
        background: 'rgba(1, 58, 111, 0.15)',
        borderBottom: '1px solid rgba(1, 58, 111, 0.50)',
        display: 'flex',
        pageBreakInside: 'avoid',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(0, 1.25),
        margin: theme.spacing(2, 0),
      },
      caps: {
        fontSize: '11pt',
        fontWeight: 600,
        letterSpacing: '1px',
        color: theme.palette.highlightIcon.main,
        textTransform: 'uppercase',
      },
      pageTitle: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: theme.spacing(0.5),
      },
      titleText: {
        fontWeight: 800,
        fontSize: pxToRem(18),
        color: theme.palette.highlightIcon.main,
      },
      titleIcon: {
        width: 16,
        height: 16,
      },
      titleSuccessIcon: {
        color: theme.palette.success.main,
      },
      titleFailIcon: {
        color: theme.palette.error.main,
      },
      titleVerificationText: {
        textTransform: 'uppercase',
        fontWeight: 800,
        fontSize: pxToRem(11),
      },
      verifiedText: {
        color: theme.palette.success.dark,
      },
      unVerifiedText: {
        color: theme.palette.error.dark,
      },
      verificationWrapper: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
      },
    }),
  );

  const classes = useStyles();

  useEffect(() => {
    executeIntelliCheckReport().then();
    if (fileId && transactionId) {
      document.title = `Intellicheck Report - ${fileId} - ${transactionId}`;
    }
  }, [fileId, transactionId]);

  useEffect(() => {
    const normalized = normalizeKeysToCamelCase(intelliCheckReportData);
    setReportData(normalized);
  }, [intelliCheckReportData]);

  return (
    <div className={classes.fullWidth}>
      {reportData && (
        <div id="pdf-content" className={classes.pdfbody}>
          <>
            <Grid item sm={12} className={classes.pageTitle}>
              <Typography className={classes.titleText}>Identity Verification Report</Typography>
            </Grid>

            {/* Summary Info */}
            <div className={classes.blueBar}>
              <p className={classes.caps}>Summary</p>
            </div>
            <SummaryInfo summary={reportData.summary} />

            {/* Scan Images */}
            <ScanImages {...reportData.reportData?.images} />

            {/* Barcode Scan Results */}
            <div className={classes.blueBar}>
              <p className={classes.caps}>Barcode Scan Results</p>
            </div>
            <BarcodeScanInfo
              barcodeScan={reportData.reportData?.idcheck.data}
              phoneNumber={reportData.reportData?.ipqs.phoneDetails.data.formatted}
              ocrMatchData={reportData.reportData?.ocrMatch.data}
            />
          </>
        </div>
      )}
    </div>
  );
};

export default IntellicheckReport;
